import React, { useEffect, useMemo, useState } from 'react';

export default function KpiModal({
                                     show,
                                     mode = 'create',           // 'create' | 'edit'
                                     onClose,
                                     onSubmit,                   // (dto) => Promise
                                     initial = null,
                                     remaining = 100,
                                 }) {
    const isEdit = mode === 'edit';

    const [name, setName] = useState('');
    const [weight, setWeight] = useState('');
    const [threshold, setThreshold] = useState('');
    const [target, setTarget] = useState('');
    const [stretch, setStretch] = useState('');
    const [isPct, setIsPct] = useState(false);
    const [unit, setUnit] = useState('');
    const [higherIsBetter, setHigherIsBetter] = useState(true);
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!show) return;
        setError(null);
        setSubmitting(false);
        if (isEdit && initial) {
            setName(initial.name ?? '');
            setWeight(String(initial.weight ?? ''));
            setThreshold(initial.threshold ?? '');
            setTarget(initial.target ?? '');
            setStretch(initial.stretch ?? '');
            const pct = Boolean(initial.is_percentage);
            setIsPct(pct);
            setUnit(pct ? '%' : (initial.unit ?? ''));
            setHigherIsBetter(initial.higher_is_better !== false);
            setDescription(initial.description ?? '');
        } else {
            setName('');
            setWeight('');
            setThreshold('');
            setTarget('');
            setStretch('');
            setIsPct(false);
            setUnit('');
            setHigherIsBetter(true);
            setDescription('');
        }
    }, [show, isEdit, initial]);

    const parsedWeight = useMemo(() => Number(weight) || 0, [weight]);
    const th = useMemo(() => (threshold === '' ? null : Number(threshold)), [threshold]);
    const tg = useMemo(() => (target === '' ? null : Number(target)), [target]);
    const st = useMemo(() => (stretch === '' ? null : Number(stretch)), [stretch]);

    const maxAllowed = useMemo(() => {
        if (!isEdit) return remaining;
        const current = Number(initial?.weight ?? 0);
        return Number(remaining) + current;
    }, [isEdit, remaining, initial]);

    const thresholdsOk = useMemo(() => {
        if (th != null && tg != null && th > tg) return false;
        if (tg != null && st != null && tg > st) return false;
        return true;
    }, [th, tg, st]);

    // NOTE: removed 0–100 constraint for percentages.

    const canSubmit = useMemo(() => {
        if (!name.trim()) return false;
        if (!(parsedWeight > 0)) return false;
        if (parsedWeight > maxAllowed) return false;
        if (!thresholdsOk) return false;
        return true;
    }, [name, parsedWeight, maxAllowed, thresholdsOk]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setError(null);
        try {
            const dto = {
                name: name.trim(),
                weight: parseFloat(parsedWeight.toFixed(2)),
                threshold: th,
                target: tg,
                stretch: st,
                is_percentage: isPct,
                unit: isPct ? '%' : (unit || null),
                higher_is_better: higherIsBetter,
                description: description.trim() || null,
            };
            await onSubmit(dto);
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to save KPI.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.15)' }}>
            <div className="modal-dialog modal-lg" role="document">
                <form className="modal-content" onSubmit={handleSubmit}>
                    <div className="modal-header">
                        <h5 className="modal-title">{isEdit ? 'Edit KPI' : 'Add KPI'}</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}

                        <div className="row g-3">
                            <div className="col-md-7">
                                <label className="form-label">Name</label>
                                <input className="form-control" value={name} onChange={e => setName(e.target.value)} required />
                            </div>

                            <div className="col-md-5">
                                <label className="form-label">Weight (≤ {Number(maxAllowed).toFixed(2)})</label>
                                <input
                                    type="number"
                                    className={`form-control ${parsedWeight > maxAllowed ? 'is-invalid' : ''}`}
                                    value={weight}
                                    onChange={e => setWeight(e.target.value)}
                                    step="0.01" min="0" required
                                />
                                {parsedWeight > maxAllowed && (
                                    <div className="invalid-feedback">This exceeds the allowed {Number(maxAllowed).toFixed(2)}%</div>
                                )}
                            </div>

                            <div className="col-md-4">
                                <label className="form-label">Threshold</label>
                                <input type="number" className="form-control" value={threshold} onChange={e => setThreshold(e.target.value)} step="0.01" />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Target</label>
                                <input type="number" className="form-control" value={target} onChange={e => setTarget(e.target.value)} step="0.01" />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Stretch</label>
                                <input type="number" className="form-control" value={stretch} onChange={e => setStretch(e.target.value)} step="0.01" />
                            </div>

                            <div className="col-md-3 d-flex align-items-center">
                                <div className="form-check mt-3">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="kpiPct"
                                        checked={isPct}
                                        onChange={e => setIsPct(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="kpiPct">Is percentage</label>
                                </div>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Unit</label>
                                <input
                                    className="form-control"
                                    value={isPct ? '%' : unit}
                                    onChange={e => setUnit(e.target.value)}
                                    disabled={isPct}
                                    placeholder={isPct ? '%' : ''}
                                />
                            </div>

                            <div className="col-md-6 d-flex align-items-center">
                                <div className="form-check mt-3">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="kpiHigherBetter"
                                        checked={higherIsBetter}
                                        onChange={e => setHigherIsBetter(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="kpiHigherBetter">
                                        Higher is better
                                    </label>
                                </div>
                            </div>

                            <div className="col-12">
                                <label className="form-label">Description (optional)</label>
                                <textarea className="form-control" rows={3} value={description} onChange={e => setDescription(e.target.value)} />
                            </div>

                            {!thresholdsOk && (
                                <div className="col-12">
                                    <div className="alert alert-warning py-2 px-3 mb-0">
                                        Constraint: threshold ≤ target ≤ stretch
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={!canSubmit || submitting}>
                            {submitting ? 'Saving…' : (isEdit ? 'Save Changes' : 'Save KPI')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
